import React, { useState } from "react";
import Panel from "components/Panel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faCircleDot } from "@fortawesome/pro-regular-svg-icons";
import {
  faCircle1,
  faCircle2,
  faCircle3,
  faCircleCheck,
  faCircleX,
  faSpinner,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import Button from "components/Button";
import CopyToClipboard from "components/CopyToClipboard";

import { WorkbookType, WorkbookVariantType } from "../Workbook";
import { useCreateWorkbookResultMutation } from "./gql/Mutation.create.generated";
import { useCreateWorkbookCollectorRunMutation } from "./gql/Mutation.collector.generated";
import {
  WorkbookVariantQuery,
  useWorkbookVariantQuery,
} from "./gql/Query.generated";
type WorkbookResultType =
  WorkbookVariantQuery["getWorkbookVariant"]["workbookResults"][number];

import { Link, useNavigate } from "react-router-dom";
import { useRoutes } from "utils/routes";
import Grid, { CellData } from "components/Grid";
import Callout from "components/Callout";
import PageContent from "components/PageContent";
import { QueryTextArea } from "../ReviewQuery";
import PillButtonBar from "components/PillButtonBar";
import { formatMs, formatTimeAgo } from "utils/format";
import Loading from "components/Loading";
import {
  CreateSteps,
  WorkbookCreationHeaderWithWorkbook,
} from "../ChooseParameters";
import { usePauseAfter } from "utils/hooks";
import {
  CreateVariantSteps,
  VariantCreationHeaderWithVariant,
} from "../RewriteQuery";
import PanelSection from "components/PanelSection";
import Tip from "components/Tip";
import classNames from "classnames";

type RunMethodType = "manual" | "collector";
type RunState =
  | "pending"
  | "success"
  | "success (no analyze)"
  | "errored"
  | "running";
const NO_PARAM_ID = "no param set";

type RunDataType = {
  setId: string;
  name: string;
  state: RunState;
  submitted: number | null;
  runtime: number | null;
  errorMessage: string | null;
};

type CellRendererProps = CellData<
  RunDataType,
  "name" | "submitted" | "runtime"
> & {
  row: number;
  column: number;
  title?: string;
  className: string;
  children: React.ReactNode;
};

const RunExplain = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const baseline = workbook.baselineQuery.id === variant.id;
  const workbookTitle = baseline ? (
    <WorkbookCreationHeaderWithWorkbook workbook={workbook} />
  ) : (
    <VariantCreationHeaderWithVariant variant={variant} />
  );

  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      featureInfo={workbookTitle}
      pageCategory="workbooks"
      pageName="run-explain"
    >
      <RunExplainContent workbook={workbook} variant={variant} />
    </PageContent>
  );
};

const RunExplainContent = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const supportedCollector = workbook.server.collectorInfo?.supportsQueryTuning;
  const [runMethod, setRunMethod] = useState<RunMethodType>(
    supportedCollector && workbook.explainAnalyzeHelperSource
      ? "collector"
      : "manual",
  );
  const baseline = variant.id === workbook.baselineQuery.id;
  return (
    <Panel
      title={
        baseline ? (
          <CreateSteps step="step3" workbook={workbook} />
        ) : (
          <CreateVariantSteps step="step2" variant={variant} />
        )
      }
    >
      <PanelSection>
        <PillButtonBar
          opts={[
            { value: "manual", label: "Manual Workflow" },
            { value: "collector", label: "Collector Workflow" },
          ]}
          selected={runMethod}
          onChange={(value) => {
            setRunMethod(value);
          }}
          mode="stretch"
        />
      </PanelSection>
      {runMethod === "manual" ? (
        <RunExplainManual workbook={workbook} variant={variant} />
      ) : (
        <RunExplainCollector workbook={workbook} variantId={variant.id} />
      )}
    </Panel>
  );
};

const RunExplainManual = ({
  workbook,
  variant,
}: {
  workbook: WorkbookType;
  variant: WorkbookVariantType;
}) => {
  const navigate = useNavigate();
  const [workbookResult, setWorkbookResult] = useState("");
  const [errorMessage, setErrorMessage] = useState<React.ReactNode>("");
  const { databaseWorkbookVariant } = useRoutes();

  const noParamSets = workbook.parameterSets.length === 0;

  const [createWorkbookResult] = useCreateWorkbookResultMutation();

  const allSubmitted = noParamSets
    ? variant.workbookResults.length === 1
    : variant.workbookResults.length === workbook.parameterSets.length;
  const resultFromCollector = variant.workbookResults.some(
    (result) => result.resultSource === "collector_upload",
  );
  const allExecuted =
    variant.workbookResults.length > 0 &&
    variant.workbookResults.every((result) => result.status === "synced");
  const [focusedSetId, setFocusedSetId] = useState(
    allSubmitted
      ? ""
      : noParamSets
      ? NO_PARAM_ID
      : workbook.parameterSets[0].id,
  );

  // Set statement timeout to 1 minute
  const plannerSettingsCommand = Object.entries(
    variant.plannerSettings as { [key: string]: any },
  ).map(([key, value]) => {
    return `SET ${key} = ${value};`;
  });
  const resetPlannerSettingsCommand = Object.entries(
    variant.plannerSettings as { [key: string]: any },
  ).map(([key, _]) => {
    return `RESET ${key};`;
  });
  const parameterSet = workbook.parameterSets.find(
    (set) => set.id === focusedSetId,
  );
  const parameterSetComment = parameterSet
    ? `--- ${parameterSet.name}`
    : undefined;
  const stmtTimeoutCommand = "SET statement_timeout = 60000;";
  const explainCommand = "EXPLAIN (ANALYZE, VERBOSE, BUFFERS, FORMAT JSON)";
  const WorkbookVariantText = noParamSets
    ? variant.queryText
    : variant.queryTextWithParameters.find(
        (val) => val.parameterSetId === focusedSetId,
      )?.queryWithParameters;
  const queryTextWithSemicolon = `${WorkbookVariantText}${
    WorkbookVariantText?.endsWith(";") ? "" : ";"
  }`;
  const explainWithQueryText = [
    parameterSetComment,
    ...plannerSettingsCommand,
    stmtTimeoutCommand,
    explainCommand,
    queryTextWithSemicolon,
    ...resetPlannerSettingsCommand,
  ]
    .filter(Boolean)
    .join("\n");

  const validateWorkbookResult = (
    result: string,
  ): { format: "json" | "text" | null } => {
    const resultExample = `[
  {
    "Plan": {
      "Node Type": ...
    }
  }
]`;
    const invalidJSONmessage = (
      <div>
        Looks like a JSON EXPLAIN result is pasted, but the structure is
        invalid. Check the structure to make sure it is a single-element array
        containing the 'Plan' key <Tip content={<pre>{resultExample}</pre>} />.
      </div>
    );
    const hasPlanKey = result.includes(`"Plan": {`);
    try {
      const parsedJson = JSON.parse(result);
      if (parsedJson[0]?.Plan === undefined) {
        setErrorMessage(invalidJSONmessage);
        return { format: null };
      }
    } catch {
      if (hasPlanKey) {
        setErrorMessage(invalidJSONmessage);
        return { format: null };
      }
      setErrorMessage("");
      return { format: "text" };
    }
    setErrorMessage("");
    return { format: "json" };
  };

  const handleSubmitCurrentSet = () => {
    if (workbookResult === "") {
      setErrorMessage("Explain result is required");
      return;
    }
    const { format } = validateWorkbookResult(workbookResult);
    if (!format) {
      return;
    }
    const uploadedResultSetIds = variant.workbookResults.map(
      (result) => result.parameterSetId,
    );
    if (
      !uploadedResultSetIds.includes(focusedSetId) ||
      window.confirm(
        `Result for this parameter set is already uploaded. Would you like to update the result?`,
      )
    ) {
      createWorkbookResult({
        variables: {
          variantId: variant.id,
          explainJson: format === "json" ? workbookResult : "",
          explainText: format === "text" ? workbookResult : "",
          parameterSetId: focusedSetId === NO_PARAM_ID ? null : focusedSetId,
        },
        onCompleted: () => {
          setErrorMessage("");
          setWorkbookResult("");
          const nextSetId = workbook.parameterSets.find(
            (val) =>
              val.id !== focusedSetId && !uploadedResultSetIds.includes(val.id),
          )?.id;
          setFocusedSetId(nextSetId ?? "");
        },
        onError: (error) => {
          setErrorMessage(error.message);
        },
      });
    }
  };

  const runData = generateRunData(workbook, variant, noParamSets);
  const psqlFormatCommand = "\\pset tuples_only true \\pset format unaligned";

  const CellRenderer = ({
    rowData,
    title,
    className,
    children,
  }: CellRendererProps) => {
    return (
      <div
        title={title}
        className={classNames(
          className,
          "!mt-0",
          focusedSetId === rowData.setId && "bg-[#fef9c2]",
        )}
      >
        {children}
      </div>
    );
  };

  return (
    <div className="p-2.5">
      <Grid
        data={runData}
        cellRenderer={CellRenderer}
        columns={[
          {
            field: "name",
            header: "Run",
            width: "1fr",
            renderer: function NameCell({ fieldData, rowData }) {
              return (
                <Button
                  bare
                  className="cursor-pointer flex w-full"
                  onClick={() => {
                    setErrorMessage("");
                    focusedSetId === rowData.setId
                      ? setFocusedSetId("")
                      : setFocusedSetId(rowData.setId);
                  }}
                >
                  <div>
                    {focusedSetId === rowData.setId ? (
                      <FontAwesomeIcon icon={faCircleDot} />
                    ) : (
                      <RunIcon state={rowData.state} />
                    )}
                  </div>
                  <div className="ml-2">
                    <div>{fieldData}</div>
                    {rowData.errorMessage && (
                      <div className="text-[#D1242F]">
                        {rowData.errorMessage}
                      </div>
                    )}
                  </div>
                </Button>
              );
            },
            disableSort: true,
          },
          {
            field: "submitted",
            header: "Submitted",
            width: "200px",
            nullValue: "-",
            renderer: function SubmittedCell({ fieldData }) {
              return formatTimeAgo(fieldData);
            },
            disableSort: true,
          },
          {
            field: "runtime",
            header: "Runtime",
            width: "320px",
            style: "number",
            renderer: function RuntimeCell({ fieldData, rowData }) {
              if (rowData.state === "success (no analyze)") {
                return "Ran without ANALYZE";
              } else if (fieldData === null) {
                return "-";
              }
              return formatMs(fieldData);
            },
            disableSort: true,
          },
        ]}
      />
      {focusedSetId !== "" && (
        <div className="grid gap-2 p-2">
          <div>
            <div className="mb-2">
              <div className="mb-1">
                <FontAwesomeIcon icon={faCircle1} /> Update psql settings so
                EXPLAIN output is easier to copy and paste
                <CopyToClipboard
                  content={psqlFormatCommand}
                  label="Copy command"
                  className="ml-1"
                />
              </div>
              <QueryTextArea
                queryText={psqlFormatCommand}
                className="!min-h-[36px] overflow-y-scroll bg-[#F9FAFB]"
                showCopyToClipboard
              />
            </div>
            <div>
              <div className="mb-1">
                <FontAwesomeIcon icon={faCircle2} /> Run EXPLAIN ANALYZE and
                paste the output below
                <CopyToClipboard
                  content={explainWithQueryText}
                  label="Copy command"
                  className="ml-1"
                />
              </div>
              <QueryTextArea
                queryText={explainWithQueryText}
                className="max-h-[200px] overflow-y-scroll bg-[#F9FAFB]"
                showCopyToClipboard
              />
            </div>
          </div>
          <div>
            In case you run into a query timeout, run the command without
            ANALYZE and paste the output.
          </div>
          <div className="flex justify-between">
            <div>
              <FontAwesomeIcon icon={faCircle3} /> Paste EXPLAIN output
            </div>
            <div className="text-[#606060]">JSON or Text format supported</div>
          </div>
          <div>
            <textarea
              rows={10}
              className="bg-white rounded border border-[#ddd] box-border w-full leading-5 px-2 py-1.5 disabled:bg-[#eee]"
              onChange={(e) => {
                setWorkbookResult(e.target.value);
                validateWorkbookResult(e.target.value);
              }}
              value={workbookResult}
              placeholder={`Paste EXPLAIN output here...\n[\n  {\n    "Plan": {\n      "Node Type": ...`}
            />
          </div>
          {errorMessage !== "" && (
            <div>
              <Callout variant="error" thin>
                {errorMessage}
              </Callout>
            </div>
          )}
          <div>
            <Button
              bare
              className="btn btn-success"
              onClick={handleSubmitCurrentSet}
            >
              Continue
            </Button>
          </div>
        </div>
      )}
      {allSubmitted && (
        <div className="pt-2">
          <Button
            bare
            className="btn btn-success"
            onClick={() =>
              navigate(
                databaseWorkbookVariant(
                  workbook.databaseId,
                  workbook.id,
                  variant.id,
                ),
              )
            }
            disabled={resultFromCollector && !allExecuted}
          >
            Explore workbook
          </Button>
        </div>
      )}
    </div>
  );
};

const POLL_INTERVAL_MS = 5000; // 5 seconds
const POLL_INTERVAL_SHORT_MS = 1000; // 1 second
const AUTOMATIC_PAUSE_INTERVAL_MS = POLL_INTERVAL_MS * 12 * 10; // 10 minutes

const RunExplainCollector = ({
  workbook,
  variantId,
}: {
  workbook: WorkbookType;
  variantId: string;
}) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [paused, setPaused] = usePauseAfter(AUTOMATIC_PAUSE_INTERVAL_MS);

  const { databaseWorkbookVariant } = useRoutes();
  const [createWorkbookCollectorRun] = useCreateWorkbookCollectorRunMutation();
  const { data, loading, error, startPolling, stopPolling } =
    useWorkbookVariantQuery({
      variables: { variantId: variantId },
      pollInterval: POLL_INTERVAL_MS,
    });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const variant = data.getWorkbookVariant as WorkbookVariantType;
  const supportedCollector = workbook.server.collectorInfo?.supportsQueryTuning;
  const supportCollectorWorkflow =
    supportedCollector && workbook.explainAnalyzeHelperSource;
  const outdatedHelper = workbook.explainAnalyzeHelperSource?.includes(
    "SELECT COALESCE('(' || pg_catalog.string_agg(pg_catalog.quote_ident(p), ',') || ')', '') FROM pg_catalog.unnest(param_types) _(p) INTO param_types_str;",
  );

  const noneExecuted = variant.workbookResults.length === 0;
  const allExecuted =
    variant.workbookResults.length > 0 &&
    variant.workbookResults.length === workbook.parameterSets.length &&
    variant.workbookResults.every(
      (result) => result.status === "synced" || result.status === "error",
    );
  const allSucceeded =
    allExecuted &&
    variant.workbookResults.every((result) => result.status === "synced");
  // Case when a new parameter set is added, so there are some parameter sets without explain results (query runs)
  const parameterSetWithoutResultsExists =
    variant.workbookResults.length < workbook.parameterSets.length;
  if (!supportCollectorWorkflow || allExecuted || paused) {
    stopPolling();
  }
  const collectPlanRunnable =
    supportCollectorWorkflow &&
    (noneExecuted ||
      (allExecuted && !allSucceeded) ||
      parameterSetWithoutResultsExists);

  const noParamSets = workbook.parameterSets.length === 0;
  const runData = generateRunData(workbook, variant, noParamSets);

  return (
    <div className="p-2.5">
      {!supportCollectorWorkflow && (
        <Callout variant="warning" className="mb-4">
          {!supportedCollector && (
            <div>
              Unable to run the Collector Workflow: the collector is either not
              running, running a version below 0.64.1, or not connected over
              Websocket. See{" "}
              <a
                target="_blank"
                rel="noopener"
                href="https://pganalyze.com/docs/collector/upgrading"
              >
                upgrade instructions
              </a>
              .
            </div>
          )}
          {!workbook.explainAnalyzeHelperSource && (
            <div>
              The setup for the Collector Workflow is incomplete: the
              <code>pganalyze.explain_analyze</code> helper function was not
              found in this database. Follow the{" "}
              <a
                target="_blank"
                rel="noopener"
                href="https://pganalyze.com/docs/query-tuning/collector-workflow"
              >
                setup documentation
              </a>{" "}
              to enable the Collector Workflow.
            </div>
          )}
          <div className="mt-2">
            After completing setup, you will need to wait for up to ten minutes,
            then reload the page.
          </div>
        </Callout>
      )}
      {supportedCollector && outdatedHelper && (
        <Callout variant="warning" className="mb-4">
          <div>
            The <code>pganalyze.explain_analyze</code> helper function is
            outdated. Follow the{" "}
            <a
              target="_blank"
              rel="noopener"
              href="https://pganalyze.com/docs/query-tuning/collector-workflow"
            >
              setup documentation
            </a>{" "}
            to recreate and update the helper function.
          </div>
        </Callout>
      )}
      {supportCollectorWorkflow && paused && !allExecuted && (
        <Callout className="mb-2.5" variant="warning" thin>
          Automatic refresh paused.{" "}
          <Button
            bare
            onClick={() => {
              setPaused(false);
              startPolling(POLL_INTERVAL_SHORT_MS);
            }}
          >
            Click here to resume.
          </Button>
        </Callout>
      )}
      <div className="pb-4">
        <QueryTextArea
          queryText={variant.queryTextWithAlias}
          className="max-h-[200px] overflow-y-scroll bg-[#F9FAFB]"
        />
      </div>
      <Grid
        data={runData}
        columns={[
          {
            field: "name",
            header: "Run",
            width: "1fr",
            renderer: function NameCell({ fieldData, rowData }) {
              return (
                <div className="flex">
                  <div>
                    <RunIcon state={rowData.state} />
                  </div>
                  <div className="ml-2 text-wrap">
                    <div>{fieldData}</div>
                    <ErrorMessageWithNotifyUpdateFunction
                      errorMessage={rowData.errorMessage}
                      outdatedHelper={outdatedHelper}
                    />
                  </div>
                </div>
              );
            },
          },
          {
            field: "submitted",
            header: "Executed",
            width: "200px",
            nullValue: "-",
            renderer: function SubmittedCell({ fieldData }) {
              return formatTimeAgo(fieldData);
            },
          },
          {
            field: "runtime",
            header: "Runtime",
            width: "320px",
            style: "number",
            renderer: function RuntimeCell({ fieldData, rowData }) {
              if (rowData.state === "success (no analyze)") {
                return "Ran without ANALYZE due to timeout";
              } else if (fieldData === null) {
                return "-";
              }
              return formatMs(fieldData);
            },
          },
        ]}
      />
      {errorMessage !== "" && (
        <div className="pt-2">
          <Callout variant="error" thin>
            {errorMessage}
          </Callout>
        </div>
      )}
      <div className="pt-4">
        {allSucceeded ? (
          <Link
            className="btn btn-success"
            to={databaseWorkbookVariant(
              workbook.databaseId,
              workbook.id,
              variant.id,
            )}
          >
            Explore workbook
          </Link>
        ) : (
          <Button
            bare
            className="btn btn-success"
            onClick={() => {
              createWorkbookCollectorRun({
                variables: { variantId: variant.id },
                onCompleted: () => {
                  setErrorMessage("");
                  setPaused(false);
                  startPolling(POLL_INTERVAL_SHORT_MS);
                },
                onError: (error) => {
                  setErrorMessage(error.message);
                },
              });
            }}
            disabled={!collectPlanRunnable}
          >
            Collect Plans
          </Button>
        )}
      </div>
    </div>
  );
};

const ErrorMessageWithNotifyUpdateFunction = ({
  errorMessage,
  outdatedHelper,
}: {
  errorMessage: string;
  outdatedHelper: boolean;
}) => {
  if (!errorMessage) {
    return <></>;
  }
  const errorDueToOutdatedHelper =
    errorMessage.includes(
      `wrong number of parameters for prepared statement "pganalyze_explain_analyze"`,
    ) || errorMessage.match(/type "([a-z_][a-z0-9_]*)\[\]" does not exist/i);
  return (
    <>
      <div className="text-[#D1242F]">{errorMessage}</div>
      {outdatedHelper && errorDueToOutdatedHelper && (
        <div className="text-[#D1242F]">
          Your pganalyze.explain_analyze helper function definition is outdated
          and causing this error. Please recreate the helper function and try
          again.{" "}
          <a
            target="_blank"
            rel="noopener"
            href="https://pganalyze.com/docs/query-tuning/collector-workflow#setting-up-the-pganalyzeexplain_analyze-helper"
          >
            Check the definition in the docs.
          </a>
        </div>
      )}
    </>
  );
};

const RunIcon = ({ state }: { state: RunState }) => {
  let icon = faCircle;
  let color = "text-[#E8E8EE]";
  let title = "pending";
  if (state === "running") {
    icon = faSpinner;
    color = "text-link animate-spin-slow";
    title = "running";
  } else if (state === "errored") {
    icon = faCircleX;
    color = "text-[#D1242F]";
    title = "errored";
  } else if (state === "success") {
    icon = faCircleCheck;
    color = "text-[#5CB85C]";
    title = "success";
  } else if (state === "success (no analyze)") {
    icon = faTriangleExclamation;
    color = "text-[#CA8A04]";
    title = "success (no analyze)";
  }
  return <FontAwesomeIcon icon={icon} className={color} title={title} />;
};

function getState(result: WorkbookResultType): RunState {
  if (!result) {
    return "pending";
  }
  if (result.runtimeMs) {
    return "success";
  }
  if (result.resultSource === "collector_upload") {
    if (result.status === "error") {
      return "errored";
    } else if (result.status === "synced") {
      // synced but with no runtimeMs
      return "success (no analyze)";
    } else {
      return "running";
    }
  }
  return "success (no analyze)";
}

function generateRunData(
  workbook: WorkbookType,
  variant: WorkbookVariantType,
  noParamSets: boolean,
): RunDataType[] {
  const firstResult = variant.workbookResults[0];

  return noParamSets
    ? [
        {
          setId: NO_PARAM_ID,
          name: "Baseline Run",
          state: getState(firstResult),
          submitted: submittedTime(firstResult),
          runtime: firstResult ? firstResult.runtimeMs : null,
          errorMessage: firstResult?.errorMessage,
        },
      ]
    : workbook.parameterSets.map((paramSet) => {
        const result = variant.workbookResults.find(
          (res) => res.parameterSetId === paramSet.id,
        );
        return {
          setId: paramSet.id,
          name: paramSet.name,
          state: getState(result),
          submitted: submittedTime(result),
          runtime: result ? result.runtimeMs : null,
          errorMessage: result?.errorMessage,
        };
      });
}

function submittedTime(result: WorkbookResultType | null): number | null {
  if (
    !result ||
    (result.resultSource === "collector_upload" && !result.status)
  ) {
    return null;
  }
  return result.updatedAt * 1000;
}

export default RunExplain;
